/**
 * Attaching and detaching the file-upload data sources on one report.
 *
 * The two sources are separate endpoints on the API rather than one parameterised route,
 * because each parses and aggregates its own shape. They are unified here so the panel can
 * treat a source as a value.
 */
import axios from 'axios';
import { AnalysisService } from '@inference.engine/api-client/web';
import type { IqDataSource } from '@/entities/iq-data-source';
import { inferenceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

/**
 * Attaches a file to one source.
 *
 * The global toast is suppressed: when the parser rejects a file it names the columns it
 * could not find, and that sentence is the only thing telling the user how to fix the file.
 * It belongs in the panel next to the drop zone, not in a toast that scrolls away — so the
 * detail is rethrown for the caller to render.
 */
export const uploadDataSourceFile = async (
  analysisId: string,
  source: IqDataSource,
  file: File,
): Promise<void> => {
  const options = {
    client: inferenceHttp,
    path: { id: analysisId },
    body: { file },
    throwOnError: true,
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' } as never,
  } as const;

  try {
    if (source === 'yougov') {
      await AnalysisService.uploadYougov(options);
      return;
    }
    await AnalysisService.uploadPathmatics(options);
  } catch (error) {
    if (axios.isAxiosError<{ detail?: string }>(error)) {
      // The generic narrows `data` as required, but a 5xx with an empty body yields
      // undefined and a body deserialising to literal null yields null, either of which
      // would throw here without the guard. Same pattern as `useSubmitBrief`.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const detail = error.response?.data?.detail;
      if (detail) throw new Error(detail, { cause: error });
    }
    throw error;
  }
};

export const deleteDataSourceFile = async (
  analysisId: string,
  source: IqDataSource,
): Promise<void> => {
  const options = {
    client: inferenceHttp,
    path: { id: analysisId },
    throwOnError: true,
  } as const;

  if (source === 'yougov') {
    await AnalysisService.deleteYougov(options);
    return;
  }
  await AnalysisService.deletePathmatics(options);
};

/**
 * A signed URL for the stored original. Short-lived, so it is resolved at the moment the
 * user asks for it rather than held on the loaded report.
 */
export const getDataSourceFileUrl = async (
  analysisId: string,
  source: IqDataSource,
): Promise<string> => {
  const options = {
    client: inferenceHttp,
    path: { id: analysisId },
    throwOnError: true,
  } as const;

  const response =
    source === 'yougov'
      ? await AnalysisService.yougovFile(options)
      : await AnalysisService.pathmaticsFile(options);

  return response.data.url;
};
